// Verified Sanskrit paradigm tables (declension + conjugation), stored as full
// surface forms so the workbook engine can generate AND grade real forms without
// a fragile sandhi generator. Every form is NFC; IAST is lowercase. The engine
// computes the syncretism index (which slots share a surface) from these cells —
// that index is what guarantees "produce the form" has one answer and "identify
// the feature" is never asked on an ambiguous axis.
//
// Forms here are the standard paradigms (Kale / Deshpande first-year scope) and
// mirror the verified curriculum specs. A seed-time test asserts NFC + lowercase.

import type { FeatureTuple, GrammaticalCase, GrammaticalNumber, GrammaticalPerson } from '@vedaunion/shared';
import type { Lemma, ParadigmEntry } from './types';

type Pair = [deva: string, iast: string];

/** Build a nominal paradigm entry from a {slot: [deva, iast]} table. */
function noun(lemma: Lemma, table: Record<string, Pair>): ParadigmEntry {
  const cells = Object.entries(table).map(([slot, [deva, iast]]) => {
    const [c, n] = slot.split('.') as [GrammaticalCase, GrammaticalNumber];
    const feature: FeatureTuple = { case: c, number: n };
    return { slot, feature, deva, iast };
  });
  return { lemma, cells };
}

/** Build a verbal paradigm entry from a {slot: [deva, iast]} table. */
function verb(lemma: Lemma, table: Record<string, Pair>): ParadigmEntry {
  const cells = Object.entries(table).map(([slot, [deva, iast]]) => {
    const [p, n] = slot.split('.') as [GrammaticalPerson, GrammaticalNumber];
    const feature: FeatureTuple = { person: p, number: n };
    return { slot, feature, deva, iast };
  });
  return { lemma, cells };
}

// ---- a-stem masculine: देव (deva) "god" -----------------------------------
const DEVA = noun(
  { id: 'deva', stemClass: 'a-masc', deva: 'देव', iast: 'deva', gloss: 'god' },
  {
    'nom.sg': ['देवः', 'devaḥ'], 'acc.sg': ['देवम्', 'devam'], 'ins.sg': ['देवेन', 'devena'],
    'dat.sg': ['देवाय', 'devāya'], 'abl.sg': ['देवात्', 'devāt'], 'gen.sg': ['देवस्य', 'devasya'],
    'loc.sg': ['देवे', 'deve'], 'voc.sg': ['देव', 'deva'],
    'nom.dual': ['देवौ', 'devau'], 'acc.dual': ['देवौ', 'devau'], 'ins.dual': ['देवाभ्याम्', 'devābhyām'],
    'dat.dual': ['देवाभ्याम्', 'devābhyām'], 'abl.dual': ['देवाभ्याम्', 'devābhyām'], 'gen.dual': ['देवयोः', 'devayoḥ'],
    'loc.dual': ['देवयोः', 'devayoḥ'], 'voc.dual': ['देवौ', 'devau'],
    'nom.pl': ['देवाः', 'devāḥ'], 'acc.pl': ['देवान्', 'devān'], 'ins.pl': ['देवैः', 'devaiḥ'],
    'dat.pl': ['देवेभ्यः', 'devebhyaḥ'], 'abl.pl': ['देवेभ्यः', 'devebhyaḥ'], 'gen.pl': ['देवानाम्', 'devānām'],
    'loc.pl': ['देवेषु', 'deveṣu'], 'voc.pl': ['देवाः', 'devāḥ'],
  },
);

// ---- a-stem masculine: नर (nara) "man" — second a-masc for variation -------
const NARA = noun(
  { id: 'nara', stemClass: 'a-masc', deva: 'नर', iast: 'nara', gloss: 'man' },
  {
    'nom.sg': ['नरः', 'naraḥ'], 'acc.sg': ['नरम्', 'naram'], 'ins.sg': ['नरेण', 'nareṇa'],
    'dat.sg': ['नराय', 'narāya'], 'abl.sg': ['नरात्', 'narāt'], 'gen.sg': ['नरस्य', 'narasya'],
    'loc.sg': ['नरे', 'nare'], 'voc.sg': ['नर', 'nara'],
    'nom.dual': ['नरौ', 'narau'], 'acc.dual': ['नरौ', 'narau'], 'ins.dual': ['नराभ्याम्', 'narābhyām'],
    'dat.dual': ['नराभ्याम्', 'narābhyām'], 'abl.dual': ['नराभ्याम्', 'narābhyām'], 'gen.dual': ['नरयोः', 'narayoḥ'],
    'loc.dual': ['नरयोः', 'narayoḥ'], 'voc.dual': ['नरौ', 'narau'],
    'nom.pl': ['नराः', 'narāḥ'], 'acc.pl': ['नरान्', 'narān'], 'ins.pl': ['नरैः', 'naraiḥ'],
    'dat.pl': ['नरेभ्यः', 'narebhyaḥ'], 'abl.pl': ['नरेभ्यः', 'narebhyaḥ'], 'gen.pl': ['नराणाम्', 'narāṇām'],
    'loc.pl': ['नरेषु', 'nareṣu'], 'voc.pl': ['नराः', 'narāḥ'],
  },
);

// ---- a-stem neuter: फल (phala) "fruit" ------------------------------------
const PHALA = noun(
  { id: 'phala', stemClass: 'a-neut', deva: 'फल', iast: 'phala', gloss: 'fruit' },
  {
    'nom.sg': ['फलम्', 'phalam'], 'acc.sg': ['फलम्', 'phalam'], 'ins.sg': ['फलेन', 'phalena'],
    'dat.sg': ['फलाय', 'phalāya'], 'abl.sg': ['फलात्', 'phalāt'], 'gen.sg': ['फलस्य', 'phalasya'],
    'loc.sg': ['फले', 'phale'], 'voc.sg': ['फल', 'phala'],
    'nom.dual': ['फले', 'phale'], 'acc.dual': ['फले', 'phale'], 'ins.dual': ['फलाभ्याम्', 'phalābhyām'],
    'dat.dual': ['फलाभ्याम्', 'phalābhyām'], 'abl.dual': ['फलाभ्याम्', 'phalābhyām'], 'gen.dual': ['फलयोः', 'phalayoḥ'],
    'loc.dual': ['फलयोः', 'phalayoḥ'], 'voc.dual': ['फले', 'phale'],
    'nom.pl': ['फलानि', 'phalāni'], 'acc.pl': ['फलानि', 'phalāni'], 'ins.pl': ['फलैः', 'phalaiḥ'],
    'dat.pl': ['फलेभ्यः', 'phalebhyaḥ'], 'abl.pl': ['फलेभ्यः', 'phalebhyaḥ'], 'gen.pl': ['फलानाम्', 'phalānām'],
    'loc.pl': ['फलेषु', 'phaleṣu'], 'voc.pl': ['फलानि', 'phalāni'],
  },
);

// ---- ā-stem feminine: माला (mālā) "garland" -------------------------------
const MALA = noun(
  { id: 'mala', stemClass: 'aa-fem', deva: 'माला', iast: 'mālā', gloss: 'garland' },
  {
    'nom.sg': ['माला', 'mālā'], 'acc.sg': ['मालाम्', 'mālām'], 'ins.sg': ['मालया', 'mālayā'],
    'dat.sg': ['मालायै', 'mālāyai'], 'abl.sg': ['मालायाः', 'mālāyāḥ'], 'gen.sg': ['मालायाः', 'mālāyāḥ'],
    'loc.sg': ['मालायाम्', 'mālāyām'], 'voc.sg': ['माले', 'māle'],
    'nom.dual': ['माले', 'māle'], 'acc.dual': ['माले', 'māle'], 'ins.dual': ['मालाभ्याम्', 'mālābhyām'],
    'dat.dual': ['मालाभ्याम्', 'mālābhyām'], 'abl.dual': ['मालाभ्याम्', 'mālābhyām'], 'gen.dual': ['मालयोः', 'mālayoḥ'],
    'loc.dual': ['मालयोः', 'mālayoḥ'], 'voc.dual': ['माले', 'māle'],
    'nom.pl': ['मालाः', 'mālāḥ'], 'acc.pl': ['मालाः', 'mālāḥ'], 'ins.pl': ['मालाभिः', 'mālābhiḥ'],
    'dat.pl': ['मालाभ्यः', 'mālābhyaḥ'], 'abl.pl': ['मालाभ्यः', 'mālābhyaḥ'], 'gen.pl': ['मालानाम्', 'mālānām'],
    'loc.pl': ['मालासु', 'mālāsu'], 'voc.pl': ['मालाः', 'mālāḥ'],
  },
);

// ---- a-stem neuter: मन्दिर (mandira) "temple" — for motion-toward scenes ----
const MANDIRA = noun(
  { id: 'mandira', stemClass: 'a-neut', deva: 'मन्दिर', iast: 'mandira', gloss: 'temple' },
  {
    'nom.sg': ['मन्दिरम्', 'mandiram'], 'acc.sg': ['मन्दिरम्', 'mandiram'], 'ins.sg': ['मन्दिरेण', 'mandireṇa'],
    'dat.sg': ['मन्दिराय', 'mandirāya'], 'abl.sg': ['मन्दिरात्', 'mandirāt'], 'gen.sg': ['मन्दिरस्य', 'mandirasya'],
    'loc.sg': ['मन्दिरे', 'mandire'], 'voc.sg': ['मन्दिर', 'mandira'],
    'nom.dual': ['मन्दिरे', 'mandire'], 'acc.dual': ['मन्दिरे', 'mandire'], 'ins.dual': ['मन्दिराभ्याम्', 'mandirābhyām'],
    'dat.dual': ['मन्दिराभ्याम्', 'mandirābhyām'], 'abl.dual': ['मन्दिराभ्याम्', 'mandirābhyām'], 'gen.dual': ['मन्दिरयोः', 'mandirayoḥ'],
    'loc.dual': ['मन्दिरयोः', 'mandirayoḥ'], 'voc.dual': ['मन्दिरे', 'mandire'],
    'nom.pl': ['मन्दिराणि', 'mandirāṇi'], 'acc.pl': ['मन्दिराणि', 'mandirāṇi'], 'ins.pl': ['मन्दिरैः', 'mandiraiḥ'],
    'dat.pl': ['मन्दिरेभ्यः', 'mandirebhyaḥ'], 'abl.pl': ['मन्दिरेभ्यः', 'mandirebhyaḥ'], 'gen.pl': ['मन्दिराणाम्', 'mandirāṇām'],
    'loc.pl': ['मन्दिरेषु', 'mandireṣu'], 'voc.pl': ['मन्दिराणि', 'mandirāṇi'],
  },
);

// ---- a-stem masculine: ग्राम (grāma) "village" — masc goal for motion -------
const GRAMA = noun(
  { id: 'grama', stemClass: 'a-masc', deva: 'ग्राम', iast: 'grāma', gloss: 'village' },
  {
    'nom.sg': ['ग्रामः', 'grāmaḥ'], 'acc.sg': ['ग्रामम्', 'grāmam'], 'ins.sg': ['ग्रामेण', 'grāmeṇa'],
    'dat.sg': ['ग्रामाय', 'grāmāya'], 'abl.sg': ['ग्रामात्', 'grāmāt'], 'gen.sg': ['ग्रामस्य', 'grāmasya'],
    'loc.sg': ['ग्रामे', 'grāme'], 'voc.sg': ['ग्राम', 'grāma'],
    'nom.dual': ['ग्रामौ', 'grāmau'], 'acc.dual': ['ग्रामौ', 'grāmau'], 'ins.dual': ['ग्रामाभ्याम्', 'grāmābhyām'],
    'dat.dual': ['ग्रामाभ्याम्', 'grāmābhyām'], 'abl.dual': ['ग्रामाभ्याम्', 'grāmābhyām'], 'gen.dual': ['ग्रामयोः', 'grāmayoḥ'],
    'loc.dual': ['ग्रामयोः', 'grāmayoḥ'], 'voc.dual': ['ग्रामौ', 'grāmau'],
    'nom.pl': ['ग्रामाः', 'grāmāḥ'], 'acc.pl': ['ग्रामान्', 'grāmān'], 'ins.pl': ['ग्रामैः', 'grāmaiḥ'],
    'dat.pl': ['ग्रामेभ्यः', 'grāmebhyaḥ'], 'abl.pl': ['ग्रामेभ्यः', 'grāmebhyaḥ'], 'gen.pl': ['ग्रामाणाम्', 'grāmāṇām'],
    'loc.pl': ['ग्रामेषु', 'grāmeṣu'], 'voc.pl': ['ग्रामाः', 'grāmāḥ'],
  },
);

// ---- a-stem neuter: वन (vana) "forest" — neuter goal of motion (handout anchor) ----
const VANA = noun(
  { id: 'vana', stemClass: 'a-neut', deva: 'वन', iast: 'vana', gloss: 'forest' },
  {
    'nom.sg': ['वनम्', 'vanam'], 'acc.sg': ['वनम्', 'vanam'], 'ins.sg': ['वनेन', 'vanena'],
    'dat.sg': ['वनाय', 'vanāya'], 'abl.sg': ['वनात्', 'vanāt'], 'gen.sg': ['वनस्य', 'vanasya'],
    'loc.sg': ['वने', 'vane'], 'voc.sg': ['वन', 'vana'],
    'nom.dual': ['वने', 'vane'], 'acc.dual': ['वने', 'vane'], 'ins.dual': ['वनाभ्याम्', 'vanābhyām'],
    'dat.dual': ['वनाभ्याम्', 'vanābhyām'], 'abl.dual': ['वनाभ्याम्', 'vanābhyām'], 'gen.dual': ['वनयोः', 'vanayoḥ'],
    'loc.dual': ['वनयोः', 'vanayoḥ'], 'voc.dual': ['वने', 'vane'],
    'nom.pl': ['वनानि', 'vanāni'], 'acc.pl': ['वनानि', 'vanāni'], 'ins.pl': ['वनैः', 'vanaiḥ'],
    'dat.pl': ['वनेभ्यः', 'vanebhyaḥ'], 'abl.pl': ['वनेभ्यः', 'vanebhyaḥ'], 'gen.pl': ['वनानाम्', 'vanānām'],
    'loc.pl': ['वनेषु', 'vaneṣu'], 'voc.pl': ['वनानि', 'vanāni'],
  },
);

// ---- ā-stem feminine: सेना (senā) "army" — declines identically to mālā ----
const SENA = noun(
  { id: 'sena', stemClass: 'aa-fem', deva: 'सेना', iast: 'senā', gloss: 'army' },
  {
    'nom.sg': ['सेना', 'senā'], 'acc.sg': ['सेनाम्', 'senām'], 'ins.sg': ['सेनया', 'senayā'],
    'dat.sg': ['सेनायै', 'senāyai'], 'abl.sg': ['सेनायाः', 'senāyāḥ'], 'gen.sg': ['सेनायाः', 'senāyāḥ'],
    'loc.sg': ['सेनायाम्', 'senāyām'], 'voc.sg': ['सेने', 'sene'],
    'nom.dual': ['सेने', 'sene'], 'acc.dual': ['सेने', 'sene'], 'ins.dual': ['सेनाभ्याम्', 'senābhyām'],
    'dat.dual': ['सेनाभ्याम्', 'senābhyām'], 'abl.dual': ['सेनाभ्याम्', 'senābhyām'], 'gen.dual': ['सेनयोः', 'senayoḥ'],
    'loc.dual': ['सेनयोः', 'senayoḥ'], 'voc.dual': ['सेने', 'sene'],
    'nom.pl': ['सेनाः', 'senāḥ'], 'acc.pl': ['सेनाः', 'senāḥ'], 'ins.pl': ['सेनाभिः', 'senābhiḥ'],
    'dat.pl': ['सेनाभ्यः', 'senābhyaḥ'], 'abl.pl': ['सेनाभ्यः', 'senābhyaḥ'], 'gen.pl': ['सेनानाम्', 'senānām'],
    'loc.pl': ['सेनासु', 'senāsu'], 'voc.pl': ['सेनाः', 'senāḥ'],
  },
);

// ---- ī-stem feminine (devī-class): नदी (nadī) "river" -----------------------
// stemClass 'i-fem'. 1/1 is the bare stem (no visarga). 7/3 नदीषु shows ṣatva
// (s → ṣ after ī, Pāṇini 8.3.59). voc.sg shortens to हे नदि.
const NADI = noun(
  { id: 'nadi', stemClass: 'i-fem', deva: 'नदी', iast: 'nadī', gloss: 'river' },
  {
    'nom.sg': ['नदी', 'nadī'], 'acc.sg': ['नदीम्', 'nadīm'], 'ins.sg': ['नद्या', 'nadyā'],
    'dat.sg': ['नद्यै', 'nadyai'], 'abl.sg': ['नद्याः', 'nadyāḥ'], 'gen.sg': ['नद्याः', 'nadyāḥ'],
    'loc.sg': ['नद्याम्', 'nadyām'], 'voc.sg': ['नदि', 'nadi'],
    'nom.dual': ['नद्यौ', 'nadyau'], 'acc.dual': ['नद्यौ', 'nadyau'], 'ins.dual': ['नदीभ्याम्', 'nadībhyām'],
    'dat.dual': ['नदीभ्याम्', 'nadībhyām'], 'abl.dual': ['नदीभ्याम्', 'nadībhyām'], 'gen.dual': ['नद्योः', 'nadyoḥ'],
    'loc.dual': ['नद्योः', 'nadyoḥ'], 'voc.dual': ['नद्यौ', 'nadyau'],
    'nom.pl': ['नद्यः', 'nadyaḥ'], 'acc.pl': ['नदीः', 'nadīḥ'], 'ins.pl': ['नदीभिः', 'nadībhiḥ'],
    'dat.pl': ['नदीभ्यः', 'nadībhyaḥ'], 'abl.pl': ['नदीभ्यः', 'nadībhyaḥ'], 'gen.pl': ['नदीनाम्', 'nadīnām'],
    'loc.pl': ['नदीषु', 'nadīṣu'], 'voc.pl': ['नद्यः', 'nadyaḥ'],
  },
);

// ---- i-stem masculine: हरि (hari) "Viṣṇu" ----------------------------------
// 3/1 हरिणा with ṇatva after r. gen.dual = loc.dual = हर्योः (spec-corrected
// from the garbled source हयोः/हय्योः; hari + os → haryoḥ). loc.pl हरिषु with
// ṣatva (short i is an iṇ-trigger) per the spec's [CHECK] resolution.
const HARI = noun(
  { id: 'hari', stemClass: 'i-masc', deva: 'हरि', iast: 'hari', gloss: 'Viṣṇu' },
  {
    'nom.sg': ['हरिः', 'hariḥ'], 'acc.sg': ['हरिम्', 'harim'], 'ins.sg': ['हरिणा', 'hariṇā'],
    'dat.sg': ['हरये', 'haraye'], 'abl.sg': ['हरेः', 'hareḥ'], 'gen.sg': ['हरेः', 'hareḥ'],
    'loc.sg': ['हरौ', 'harau'], 'voc.sg': ['हरे', 'hare'],
    'nom.dual': ['हरी', 'harī'], 'acc.dual': ['हरी', 'harī'], 'ins.dual': ['हरिभ्याम्', 'haribhyām'],
    'dat.dual': ['हरिभ्याम्', 'haribhyām'], 'abl.dual': ['हरिभ्याम्', 'haribhyām'], 'gen.dual': ['हर्योः', 'haryoḥ'],
    'loc.dual': ['हर्योः', 'haryoḥ'], 'voc.dual': ['हरी', 'harī'],
    'nom.pl': ['हरयः', 'harayaḥ'], 'acc.pl': ['हरीन्', 'harīn'], 'ins.pl': ['हरिभिः', 'haribhiḥ'],
    'dat.pl': ['हरिभ्यः', 'haribhyaḥ'], 'abl.pl': ['हरिभ्यः', 'haribhyaḥ'], 'gen.pl': ['हरीणाम्', 'harīṇām'],
    'loc.pl': ['हरिषु', 'hariṣu'], 'voc.pl': ['हरयः', 'harayaḥ'],
  },
);

// ---- i-stem masculine: अग्नि (agni) "fire" — declines identically to hari ----
// 3/1 अग्निना (dental n — no preceding r/ṣ to trigger ṇatva, contrast हरिणा).
// gen.dual = loc.dual अग्न्योः (agni + os → agnyoḥ). loc.pl अग्निषु with ṣatva.
const AGNI = noun(
  { id: 'agni', stemClass: 'i-masc', deva: 'अग्नि', iast: 'agni', gloss: 'fire' },
  {
    'nom.sg': ['अग्निः', 'agniḥ'], 'acc.sg': ['अग्निम्', 'agnim'], 'ins.sg': ['अग्निना', 'agninā'],
    'dat.sg': ['अग्नये', 'agnaye'], 'abl.sg': ['अग्नेः', 'agneḥ'], 'gen.sg': ['अग्नेः', 'agneḥ'],
    'loc.sg': ['अग्नौ', 'agnau'], 'voc.sg': ['अग्ने', 'agne'],
    'nom.dual': ['अग्नी', 'agnī'], 'acc.dual': ['अग्नी', 'agnī'], 'ins.dual': ['अग्निभ्याम्', 'agnibhyām'],
    'dat.dual': ['अग्निभ्याम्', 'agnibhyām'], 'abl.dual': ['अग्निभ्याम्', 'agnibhyām'], 'gen.dual': ['अग्न्योः', 'agnyoḥ'],
    'loc.dual': ['अग्न्योः', 'agnyoḥ'], 'voc.dual': ['अग्नी', 'agnī'],
    'nom.pl': ['अग्नयः', 'agnayaḥ'], 'acc.pl': ['अग्नीन्', 'agnīn'], 'ins.pl': ['अग्निभिः', 'agnibhiḥ'],
    'dat.pl': ['अग्निभ्यः', 'agnibhyaḥ'], 'abl.pl': ['अग्निभ्यः', 'agnibhyaḥ'], 'gen.pl': ['अग्नीनाम्', 'agnīnām'],
    'loc.pl': ['अग्निषु', 'agniṣu'], 'voc.pl': ['अग्नयः', 'agnayaḥ'],
  },
);

// ---- u-stem masculine: गुरु (guru) "teacher" — parallel to hari on a -u stem ----
// 3/1 गुरुणा with ṇatva after r. gen.dual = loc.dual = गुर्वोः (spec-corrected
// from the garbled source गुवोः/गव्वोः; guru + os → gurvoḥ). loc.pl गुरुषु with
// ṣatva (short u is an iṇ-trigger) per the spec's [CHECK] resolution.
const GURU = noun(
  { id: 'guru', stemClass: 'u-masc', deva: 'गुरु', iast: 'guru', gloss: 'teacher' },
  {
    'nom.sg': ['गुरुः', 'guruḥ'], 'acc.sg': ['गुरुम्', 'gurum'], 'ins.sg': ['गुरुणा', 'guruṇā'],
    'dat.sg': ['गुरवे', 'gurave'], 'abl.sg': ['गुरोः', 'guroḥ'], 'gen.sg': ['गुरोः', 'guroḥ'],
    'loc.sg': ['गुरौ', 'gurau'], 'voc.sg': ['गुरो', 'guro'],
    'nom.dual': ['गुरू', 'gurū'], 'acc.dual': ['गुरू', 'gurū'], 'ins.dual': ['गुरुभ्याम्', 'gurubhyām'],
    'dat.dual': ['गुरुभ्याम्', 'gurubhyām'], 'abl.dual': ['गुरुभ्याम्', 'gurubhyām'], 'gen.dual': ['गुर्वोः', 'gurvoḥ'],
    'loc.dual': ['गुर्वोः', 'gurvoḥ'], 'voc.dual': ['गुरू', 'gurū'],
    'nom.pl': ['गुरवः', 'guravaḥ'], 'acc.pl': ['गुरून्', 'gurūn'], 'ins.pl': ['गुरुभिः', 'gurubhiḥ'],
    'dat.pl': ['गुरुभ्यः', 'gurubhyaḥ'], 'abl.pl': ['गुरुभ्यः', 'gurubhyaḥ'], 'gen.pl': ['गुरूणाम्', 'gurūṇām'],
    'loc.pl': ['गुरुषु', 'guruṣu'], 'voc.pl': ['गुरवः', 'guravaḥ'],
  },
);

// ---- present (laṭ) parasmaipada, class 1 thematic --------------------------
const BHU = verb(
  { id: 'bhu', stemClass: 'verb-present-active', deva: 'भू', iast: 'bhū', gloss: 'to be, become' },
  {
    'p3.sg': ['भवति', 'bhavati'], 'p3.dual': ['भवतः', 'bhavataḥ'], 'p3.pl': ['भवन्ति', 'bhavanti'],
    'p2.sg': ['भवसि', 'bhavasi'], 'p2.dual': ['भवथः', 'bhavathaḥ'], 'p2.pl': ['भवथ', 'bhavatha'],
    'p1.sg': ['भवामि', 'bhavāmi'], 'p1.dual': ['भवावः', 'bhavāvaḥ'], 'p1.pl': ['भवामः', 'bhavāmaḥ'],
  },
);
const GAM = verb(
  { id: 'gam', stemClass: 'verb-present-active', deva: 'गम्', iast: 'gam', gloss: 'to go' },
  {
    'p3.sg': ['गच्छति', 'gacchati'], 'p3.dual': ['गच्छतः', 'gacchataḥ'], 'p3.pl': ['गच्छन्ति', 'gacchanti'],
    'p2.sg': ['गच्छसि', 'gacchasi'], 'p2.dual': ['गच्छथः', 'gacchathaḥ'], 'p2.pl': ['गच्छथ', 'gacchatha'],
    'p1.sg': ['गच्छामि', 'gacchāmi'], 'p1.dual': ['गच्छावः', 'gacchāvaḥ'], 'p1.pl': ['गच्छामः', 'gacchāmaḥ'],
  },
);
const PATH = verb(
  { id: 'path', stemClass: 'verb-present-active', deva: 'पठ्', iast: 'paṭh', gloss: 'to read, recite' },
  {
    'p3.sg': ['पठति', 'paṭhati'], 'p3.dual': ['पठतः', 'paṭhataḥ'], 'p3.pl': ['पठन्ति', 'paṭhanti'],
    'p2.sg': ['पठसि', 'paṭhasi'], 'p2.dual': ['पठथः', 'paṭhathaḥ'], 'p2.pl': ['पठथ', 'paṭhatha'],
    'p1.sg': ['पठामि', 'paṭhāmi'], 'p1.dual': ['पठावः', 'paṭhāvaḥ'], 'p1.pl': ['पठामः', 'paṭhāmaḥ'],
  },
);
// √वस् vas "to dwell" — Class 1 thematic, stem वस vasa- (3 sg वसति) ‹spec §16›
const VAS = verb(
  { id: 'vas', stemClass: 'verb-present-active', deva: 'वस्', iast: 'vas', gloss: 'to dwell' },
  {
    'p3.sg': ['वसति', 'vasati'], 'p3.dual': ['वसतः', 'vasataḥ'], 'p3.pl': ['वसन्ति', 'vasanti'],
    'p2.sg': ['वससि', 'vasasi'], 'p2.dual': ['वसथः', 'vasathaḥ'], 'p2.pl': ['वसथ', 'vasatha'],
    'p1.sg': ['वसामि', 'vasāmi'], 'p1.dual': ['वसावः', 'vasāvaḥ'], 'p1.pl': ['वसामः', 'vasāmaḥ'],
  },
);
// √वद् vad "to speak" — Class 1 thematic, stem वद vada- (3 sg वदति) ‹standard›
const VAD = verb(
  { id: 'vad', stemClass: 'verb-present-active', deva: 'वद्', iast: 'vad', gloss: 'to speak' },
  {
    'p3.sg': ['वदति', 'vadati'], 'p3.dual': ['वदतः', 'vadataḥ'], 'p3.pl': ['वदन्ति', 'vadanti'],
    'p2.sg': ['वदसि', 'vadasi'], 'p2.dual': ['वदथः', 'vadathaḥ'], 'p2.pl': ['वदथ', 'vadatha'],
    'p1.sg': ['वदामि', 'vadāmi'], 'p1.dual': ['वदावः', 'vadāvaḥ'], 'p1.pl': ['वदामः', 'vadāmaḥ'],
  },
);

/** Paradigm sets referenced by a quiz step's `paradigmSet`. */
export const PARADIGM_SETS: Record<string, ParadigmEntry[]> = {
  nouns: [DEVA, NARA, GRAMA, PHALA, MANDIRA, VANA, MALA, SENA, NADI, HARI, AGNI, GURU],
  'nouns-masc': [DEVA, NARA, GRAMA, HARI, AGNI, GURU],
  'nouns-a-masc': [DEVA, NARA, GRAMA],
  'nouns-i-u': [HARI, AGNI, GURU],
  'nouns-neut': [PHALA, MANDIRA, VANA],
  // ā-stem feminine only (mālā, senā) — for the L6 checkpoint, before नदी (the
  // ī-stem) is taught in L8. 'nouns-fem' below adds NADI for the L8 checkpoint.
  'nouns-aa-fem': [MALA, SENA],
  'nouns-fem': [MALA, SENA, NADI],
  // ī-stem feminine only (नदी) — for the L8 checkpoint's nadī-specific drills.
  'nouns-ii-fem': [NADI],
  verbs: [BHU, GAM, PATH, VAS, VAD],
};

/** Flat lemma lookup across all sets (by lemma id). */
export const LEMMAS: Record<string, ParadigmEntry> = Object.fromEntries(
  Object.values(PARADIGM_SETS).flat().map((e) => [e.lemma.id, e]),
);

export function paradigmSet(key: string): ParadigmEntry[] {
  return PARADIGM_SETS[key] ?? [];
}
